import { IkeFieldType, type SubFormSpanTypeState, SubFormType } from '@ikegps/core-api';
import { IkeId } from '../../../../id';
import { FormModelFieldSelectList, type FormModelFieldSelectListOption } from '../../form.select.list';
import { SpanTypeOptionSet } from './span.type.option.set';
import { IkePoleForemanFieldIds, PoleFieldIds } from '../../../pole/integrations';
import { FormModelState } from '../../form.state';
import { type FormFieldSubFormState, FormModelFieldSubForm } from '../form.subform';
import { LOCKED_MODEL_POLE_FIELDS } from '../../form.field.lock';
import { FormModel } from '../..';

export class SpanSubFormMutator {
	private field: FormModelFieldSubForm;

	constructor(field: FormModelFieldSubForm) {
		this.field = field;
	}

	private get state(): FormModelState {
		return this.field.getForm().state;
	}

	/**
	 * Set the SubForm Type of this field to SPAN while linking the existing vector, span type,
	 * and mid span photo fields.
	 * Creates vector, type, and mid span photo fields if they don't exist.
	 */
	public enable() {
		const validator = this.field.spanValidator;
		let vectorField =
			this.field.fields.find(
				(field) => field.id === PoleFieldIds.SPAN.LENGTH || field.id === IkePoleForemanFieldIds.SPAN_LENGTH
			) ?? this.field.fields.find((field) => validator.isEligibleVectorField(field));
		let typeField =
			this.field.fields.find(
				(field) => field.id === PoleFieldIds.SPAN.TYPE || field.id === IkePoleForemanFieldIds.SPAN_TYPE
			) ?? this.field.fields.find((field) => validator.isEligibleTypeField(field));
		let environmentField =
			this.field.fields.find((field) => field.id === PoleFieldIds.SPAN.ENVIRONMENT) ??
			this.field.fields.find((field) => validator.isDefaultEnvironmentField(field)) ??
			this.field.fields.find((field) => validator.isEligibleEnvironmentField(field));
		let midSpanPhotoField =
			this.field.fields.find(
				(field) =>
					field.id === PoleFieldIds.SPAN.MID_SPAN_PHOTO || field.id === IkePoleForemanFieldIds.SPAN_MID_SPAN_IKE_PHOTO
			) ?? this.field.fields.find((field) => validator.isEligibleMidSpanPhotoField(field));

		if (!vectorField) {
			vectorField = this.addVectorField();
		}
		if (!typeField) {
			typeField = this.addSpanTypeField();
		}
		if (!environmentField) {
			environmentField = this.addEnvironmentField();
		}
		if (!midSpanPhotoField) {
			midSpanPhotoField = this.addMidSpanPhotoField();
		}

		this.setFieldReferences(vectorField.id, typeField.id, environmentField.id, midSpanPhotoField.id);
		this.updateLockState(true);
	}

	/**
	 * Disable SPAN subform type, unlocking all span fields and clearing state.
	 */
	public disable() {
		this.updateLockState(false);

		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: SubFormType.NONE,
			spanTypeState: undefined, // Clear to avoid stale field references
			...(!LOCKED_MODEL_POLE_FIELDS[this.field.id] && { locked: false }),
		} satisfies FormFieldSubFormState);
	}

	/**
	 * Set the SubForm Type of this field to SPAN.
	 *
	 * @param vectorFieldId The Span Vector field ID to use.
	 * @param typeFieldId The Span Type field ID to use.
	 * @param environmentFieldId Optional Environment field ID.
	 * @param midSpanIkePhotoFieldId Optional Mid Span IKE Photo field ID for two-way linking.
	 * @throws If any of the given field IDs do not exist in this subform.
	 */
	public setFieldReferences(
		vectorFieldId: string,
		typeFieldId: string,
		environmentFieldId?: string,
		midSpanIkePhotoFieldId?: string
	) {
		if (!this.field.hasField(vectorFieldId)) {
			throw new Error(`Vector field '${vectorFieldId}' not found`);
		}
		if (!this.field.hasField(typeFieldId)) {
			throw new Error(`Span Type field '${typeFieldId}' not found`);
		}
		if (environmentFieldId && !this.field.hasField(environmentFieldId)) {
			throw new Error(`Environment field '${environmentFieldId}' not found`);
		}
		if (midSpanIkePhotoFieldId && !this.field.hasField(midSpanIkePhotoFieldId)) {
			throw new Error(`Mid span photo field '${midSpanIkePhotoFieldId}' not found`);
		}

		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: SubFormType.SPAN,
			spanTypeState: {
				vectorFieldId,
				typeFieldId,
				environmentFieldId: typeFieldId === environmentFieldId ? undefined : environmentFieldId,
				midSpanIkePhotoFieldId,
			},
			locked: true,
		} satisfies FormFieldSubFormState);
		this.setFieldsMetadata(typeFieldId);
		this.updateLockState(true);
	}

	public setFieldsMetadata(typeFieldId: string) {
		if (typeFieldId === PoleFieldIds.SPAN.TYPE || typeFieldId === IkePoleForemanFieldIds.SPAN_TYPE) {
			this.field.getField(typeFieldId).Mutator.title(true);
		}
	}

	public selectVectorField(fieldId: string) {
		this.updateFieldReference('vectorFieldId', fieldId);
	}

	public selectSpanTypeField(fieldId: string) {
		this.updateFieldReference('typeFieldId', fieldId);
	}
	public selectEnvironmentField(fieldId: string) {
		this.updateFieldReference('environmentFieldId', fieldId);
	}

	public clearEnvironmentField() {
		const currentState = this.field.stateValue.spanTypeState;
		if (!currentState) return;

		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: SubFormType.SPAN,
			spanTypeState: {
				...currentState,
				environmentFieldId: undefined,
			},
			locked: true,
		} satisfies FormFieldSubFormState);
	}

	public selectMidSpanIkePhotoField(fieldId: string) {
		this.updateFieldReference('midSpanIkePhotoFieldId', fieldId);
	}

	private updateFieldReference(key: keyof SubFormSpanTypeState, newFieldId: string) {
		const currentState = this.field.stateValue.spanTypeState;

		if (!currentState) {
			return;
		}

		const oldFieldId = currentState?.[key];

		const newState: SubFormSpanTypeState = {
			...currentState,
			[key]: newFieldId,
		};

		if (newState.vectorFieldId && newState.typeFieldId && newFieldId) {
			this.state.setFieldValue(this.field.fieldId, {
				...this.field.stateValue,
				subFormType: SubFormType.SPAN,
				spanTypeState: newState,
				locked: true,
			} satisfies FormFieldSubFormState);
		}

		const oldField = oldFieldId ? this.field.fields.find((f) => f.id === oldFieldId) : undefined;
		const newField = this.field.fields.find((f) => f.id === newFieldId);

		if (oldField && !LOCKED_MODEL_POLE_FIELDS[oldField.id]) {
			oldField.Mutator.setLockState(false);
		}

		if (
			newField &&
			// if it is environment field, we only lock it if it is a spidacalc form
			(key !== 'environmentFieldId' || FormModel.isSpidaCalcForm(this.field.getForm()))
		) {
			newField.Mutator.setLockState(true);
		}
	}

	public addMidSpanPhotoField() {
		return this.field.createField(
			IkeFieldType.TRUE_SIZE,
			{
				id: IkeId.generate(),
				displayName: 'Mid Span IKE Photo',
				locked: true,
				isGenerated: true,
			},
			3
		);
	}

	public addVectorField() {
		return this.field.createField(
			IkeFieldType.VECTOR,
			{
				id: IkeId.generate(),
				displayName: 'Span Length',
				locked: true,
				isGenerated: true,
			},
			0
		);
	}

	public addSpanTypeField() {
		const optionSet = SpanTypeOptionSet.forForm(this.field.getForm());
		const spanTypeField = this.field.createField<FormModelFieldSelectList>(
			IkeFieldType.SELECT_LIST,
			{
				id: IkeId.generate(),
				displayName: 'Type',
				title: true,
				locked: true,
				isGenerated: true,
			},
			1
		);

		for (const role of optionSet.requiredRoles) {
			spanTypeField.Mutator.option(optionSet.optionForRole(role));
		}
		if (optionSet.building) {
			spanTypeField.Mutator.option(optionSet.building);
		}

		spanTypeField.Mutator.title(true);

		return spanTypeField;
	}

	/**
	 * Restore the assigned Type field's required options for this form's style.
	 * Wrong-style values are converted (user text preserved), missing defaults added,
	 * and options reordered to: placeholder, required roles, building, user extras.
	 */
	public repairSpanTypeField() {
		const typeFieldId = this.field.subFormSpanTypeState?.typeFieldId;
		const typeField = typeFieldId ? this.field.getOptionalField(typeFieldId) : null;
		if (!typeFieldId || !typeField || !FormModelFieldSelectList.isSelectList(typeField)) {
			return;
		}

		const optionSet = SpanTypeOptionSet.forForm(this.field.getForm());
		const remaining = typeField.options.map((option) => ({ ...option }));

		// Convert wrong-style values where the correct value is absent
		for (const role of optionSet.requiredRoles) {
			const expected = optionSet.optionForRole(role);
			if (remaining.some((option) => option.value === expected.value)) {
				continue;
			}
			const wrongStyleIndex = remaining.findIndex(
				(option) => optionSet.correctedValueFor(option.value) === expected.value
			);
			const wrongStyleOption = remaining[wrongStyleIndex];
			if (wrongStyleOption) {
				remaining[wrongStyleIndex] = { ...wrongStyleOption, value: expected.value };
			}
		}

		const takeByValue = (value: string): FormModelFieldSelectListOption | null => {
			const index = remaining.findIndex((option) => option.value === value);
			const option = remaining[index];
			if (!option) {
				return null;
			}
			remaining.splice(index, 1);
			return option;
		};

		const repaired: FormModelFieldSelectListOption[] = [];

		// Keep a leading placeholder option (e.g. "Unselected") in place
		const first = remaining[0];
		if (first && optionSet.roleForAnyStyleValue(first.value) == null && first.value !== optionSet.building?.value) {
			remaining.shift();
			repaired.push(first);
		}

		for (const role of optionSet.requiredRoles) {
			const expected = optionSet.optionForRole(role);
			repaired.push(takeByValue(expected.value) ?? expected);
		}
		if (optionSet.building) {
			const building = takeByValue(optionSet.building.value);
			if (building) {
				repaired.push(building);
			}
		}
		repaired.push(...remaining);

		typeField.Mutator.options(repaired);
		typeField.Mutator.setLockState(true);
		typeField.Mutator.title(true);
		this.setFieldsMetadata(typeFieldId);
	}

	public addEnvironmentField(index: number = 3) {
		const environmentField = this.field.createField<FormModelFieldSelectList>(
			IkeFieldType.SELECT_LIST,
			{
				id: IkeId.generate(),
				displayName: 'Environment',
				locked: FormModel.isSpidaCalcForm(this.field.getForm()),
				isGenerated: true,
			},
			index
		);

		environmentField.Mutator.option({ text: 'STREET', value: 'STREET' });
		environmentField.Mutator.option({ text: 'HIGHWAY', value: 'HIGHWAY' });
		environmentField.Mutator.option({ text: 'PEDESTRIAN', value: 'PEDESTRIAN' });
		environmentField.Mutator.option({ text: 'PARALLEL_TO_STREET', value: 'PARALLEL_TO_STREET' });
		environmentField.Mutator.option({ text: 'UNLIKELY_PARALLEL_TO_STREET', value: 'UNLIKELY_PARALLEL_TO_STREET' });
		environmentField.Mutator.option({ text: 'DRIVEWAY / VEHICLE ACCESS', value: 'DRIVEWAY / VEHICLE ACCESS' });
		environmentField.Mutator.option({ text: 'ALLEY', value: 'ALLEY' });
		environmentField.Mutator.option({ text: 'RAILROAD', value: 'RAILROAD' });
		environmentField.Mutator.option({ text: 'RURAL', value: 'RURAL' });
		environmentField.Mutator.option({ text: 'FARM', value: 'FARM' });
		environmentField.Mutator.option({ text: 'WATER_WITH SAILBOATS', value: 'WATER_WITH SAILBOATS' });
		environmentField.Mutator.option({ text: 'WATER_WITHOUT_SAILBOATS', value: 'WATER_WITHOUT_SAILBOATS' });

		return environmentField;
	}

	private updateLockState(isLocked: boolean) {
		const vectorField = this.field.fields.find((field) => field.id === this.field.subFormSpanTypeState?.vectorFieldId);
		const typeField = this.field.fields.find((field) => field.id === this.field.subFormSpanTypeState?.typeFieldId);
		const environmentField = this.field.fields.find(
			(field) => field.id === this.field.subFormSpanTypeState?.environmentFieldId
		);
		const midSpanIkePhotoField = this.field.fields.find(
			(field) => field.id === this.field.subFormSpanTypeState?.midSpanIkePhotoFieldId
		);

		if (isLocked) {
			vectorField?.Mutator.setLockState(isLocked);
			typeField?.Mutator.setLockState(isLocked);
			if (FormModel.isSpidaCalcForm(this.field.getForm())) {
				environmentField?.Mutator.setLockState(isLocked);
			}
			midSpanIkePhotoField?.Mutator.setLockState(isLocked);
			return;
		}

		// Fields with legacy-locked ids keep their lock — converted fields are not treated as generated
		if (vectorField && !LOCKED_MODEL_POLE_FIELDS[vectorField.id]) {
			vectorField.Mutator.setLockState(isLocked);
		}
		if (typeField && !LOCKED_MODEL_POLE_FIELDS[typeField.id]) {
			typeField.Mutator.setLockState(isLocked);
		}
		if (environmentField && !LOCKED_MODEL_POLE_FIELDS[environmentField.id]) {
			environmentField.Mutator.setLockState(isLocked);
		}
		if (midSpanIkePhotoField && !LOCKED_MODEL_POLE_FIELDS[midSpanIkePhotoField.id]) {
			midSpanIkePhotoField.Mutator.setLockState(isLocked);
		}
	}
}
